/**
 * Map component
 *
 * Renders a styled Google Map with a custom marker for every `.js-map` element.
 * The Maps JS API loads lazily and once, so map-less pages never pay for it.
 *
 * In-repo styling (the `styles` array below) rules out `AdvancedMarkerElement`,
 * which moves styling into a cloud Map ID, so we use the classic
 * `google.maps.Marker` - deprecated but fully supported.
 */

import $ from 'jquery';

// Pale style matched to the Figma; fine-tune the hex values in-browser.
const MAP_STYLE = [
    { elementType: 'geometry', stylers: [{ color: '#f3f1ec' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#8c8c8c' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f3f1ec' }, { weight: 2 }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e7e6df' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e8e6e0' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9b9b9b' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ededed' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d9d9d9' }] },
];

// Zoom buttons only; cooperative gestures let the page scroll past the map
// (ctrl/⌘ + scroll to zoom, two fingers to pan on touch).
const MAP_OPTIONS = {
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    clickableIcons: false,
    keyboardShortcuts: false,
};

let mapsApiPromise = null;

// Load the Maps JS API once, resolving with `google.maps`. The global callback
// is defined before the script is appended, so there's no load-order race.
function loadGoogleMaps(key) {
    if (mapsApiPromise) {
        return mapsApiPromise;
    }

    mapsApiPromise = new Promise((resolve, reject) => {
        window.__quadrantMapsReady = () => resolve(window.google.maps);

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&callback=__quadrantMapsReady`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps JS API failed to load.'));

        document.head.appendChild(script);
    });

    return mapsApiPromise;
}

export default class MapComponent {
    constructor() {
        const $maps = $('.js-map');

        if (!$maps.length) {
            return;
        }

        $maps.each((index, el) => {
            if (!el.dataset.key) {
                console.warn('Map component: no Google Maps API key set (GM_API_KEY).');
                return;
            }

            this.render(el).catch((error) => console.error(error));
        });
    }

    async render(el) {
        const lat = parseFloat(el.dataset.lat);
        const lng = parseFloat(el.dataset.lng);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return;
        }

        const position = { lat, lng };
        const maps = await loadGoogleMaps(el.dataset.key);

        const map = new maps.Map(el, {
            center: position,
            zoom: parseInt(el.dataset.zoom, 10) || 14,
            styles: MAP_STYLE,
            ...MAP_OPTIONS,
        });

        new maps.Marker({
            position,
            map,
            icon: {
                url: el.dataset.marker,
                scaledSize: new maps.Size(52, 52),
                // Anchor on the pin's bottom-right tip so it points at the location.
                anchor: new maps.Point(49, 49),
            },
        });
    }
}
